import type { Nullable } from '~/types'
import type { COUNTRY_SHORTS } from '~/constants'

type PriceDisplay = {
  value?: Nullable<number>
  countryCode?: COUNTRY_SHORTS
  showCurrencyCode?: boolean
  round?: boolean
  notation?: Intl.NumberFormatOptions['notation']
  includeDollarSign?: boolean
}

export default function usePrice() {
  const { t, locale } = useI18n({
    useScope: 'global',
  })

  function priceDisplay({
    value,
    countryCode,
    showCurrencyCode = false,
    round = false,
    notation = 'standard',
    includeDollarSign = true,
  }: PriceDisplay) {
    if (typeof value !== 'number' || isNaN(value)) {
      return ''
    }

    const internalLocale = countryCode ? `${locale.value}-${countryCode}` : locale.value
    const currencyCode = countryCode ? lookupCountryCodeToCurrencyCode(countryCode) : undefined

    const isValueNegative = value < 0

    const formatter = new Intl.NumberFormat(internalLocale, {
      minimumFractionDigits: round ? 0 : 2,
      maximumFractionDigits: round ? 0 : 2,
      notation,
    })

    const amount = Math.abs(value)
    const negativeSign = isValueNegative ? '-' : ''
    const currencyDisplay = showCurrencyCode && currencyCode ? ` ${currencyCode}` : ''
    const moneySymbolDisplay = includeDollarSign
      ? t('common.moneyDisplay', { amount: formatter.format(amount) })
      : formatter.format(amount)

    return `${negativeSign}${moneySymbolDisplay}${currencyDisplay}`
  }

  return { priceDisplay }
}
